import React, { useCallback, useEffect, useState } from "react";
import { arrayOf, bool, func, shape, string } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { goBack, push } from "react-router-redux";
import Yup from "yup";
import classNames from "classnames";

import { DebinScheduled, Box, Button, ModalWarn } from "@ui-kit/components/index.es";

import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";
import { resizableRoute } from "pages/_components/Resizable";

import { actions as debinActions, selectors as debinSelectors } from "reducers/debin";
import { actions as notificationActions } from "reducers/notification";

import * as i18n from "util/i18n";
import * as configUtils from "util/config";
import { getIncomingDebinAccountName } from "util/accounts";
import { evalEqualsValues } from "util/form";
import { applyEmbeddedV3ShellLayoutState, clearEmbeddedV3ShellLayoutState } from "util/v3ShellLayoutSync";

const getDaysAgo = () => {
    const minDate = configUtils.getInteger("frontend.debin.requests.min.dateFrom", 180);
    const date = new Date();
    date.setDate(date.getDate() - minDate);
    return date;
};

const INITIAL_FORM_VALUES = {
    cuit: "",
    dates: [getDaysAgo(), new Date()],
    debitAccount: { value: "all", label: "Todos" },
    status: { value: "all", label: "Todos" },
};

const statuses = [
    { value: "ACTIVA", label: "ACTIVA" },
    { value: "INACTIVA", label: "INACTIVA" },
    { value: "PENDIENTE", label: "PENDIENTE" },
    { value: "RECHAZADO", label: "RECHAZADO" },
];

ProgrammedDebin.propTypes = {
    dispatch: func.isRequired,
    programmedRecurrencies: arrayOf(shape({})).isRequired,
    accounts: arrayOf(
        shape({
            idProduct: string.isRequired,
        }),
    ).isRequired,
    isDesktop: bool.isRequired,
    showFilters: bool.isRequired,
    hideAmounts: bool.isRequired,
    filterRecurrencyProgrammed: shape({}).isRequired,
    fetching: bool.isRequired,
    embeddedInV3Shell: bool,
};

ProgrammedDebin.defaultProps = {
    embeddedInV3Shell: false,
};

function ProgrammedDebin({
    dispatch,
    programmedRecurrencies,
    accounts,
    isDesktop,
    showFilters,
    filterRecurrencyProgrammed,
    hideAmounts,
    fetching,
    embeddedInV3Shell,
}) {
    const [isModalWarnVisible, setIsModalWarnVisible] = useState(false);
    const [selectedDebin, setSelectedDebin] = useState(undefined);
    const [submitting, setSubmitting] = useState(false);

    function obtenerDescripcionPorCBU(cbu) {
        const account = accounts.find((obj) => obj.cbu === cbu);
        return getIncomingDebinAccountName(account);
    }

    const getStatusToDisplay = (recurrency) => {
        if (recurrency.estado === "ACTIVA" && recurrency.autorizado === "AUTORIZADO") {
            return "ACTIVA";
        }
        if (recurrency.estado === "INACTIVA" && recurrency.autorizado === "AUTORIZADO") {
            return "INACTIVA";
        }
        if (
            (recurrency.estado === "ACTIVA" || recurrency.estado === "INACTIVA") &&
            recurrency.autorizado === "PENDIENTE"
        ) {
            return "PENDIENTE";
        }
        if (
            (recurrency.estado === "ACTIVA" || recurrency.estado === "INACTIVA") &&
            recurrency.autorizado === "RECHAZADO"
        ) {
            return "RECHAZADO";
        }
        return recurrency.estado;
    };

    const isShowPendingSignature = useCallback((debin) => {
        const estado = getStatusToDisplay(debin);
        let show = false;

        if (estado === "PENDIENTE") {
            show = debin?.transactions?.some(
                (transaction) => transaction.action === "AUTORIZAR" || transaction.action === "RECHAZAR",
            );
        } else if (estado === "ACTIVA") {
            show = debin?.transactions?.some((transaction) => transaction.action === "DESACTIVAR");
        } else if (estado === "INACTIVA") {
            show = debin?.transactions?.some((transaction) => transaction.action === "ACTIVAR");
        } else if (estado === "RECHAZADO") {
            show = debin?.transactions?.some((transaction) => transaction.action === "AUTORIZAR");
        }

        return show;
    }, []);

    const formatFiltersForAPI = useCallback(
        (filters) => {
            const { cuit, dates, debitAccount, status } = filters;

            function obtenerCbuPorId(idProduct) {
                const account = accounts.find((obj) => obj.idProduct === idProduct);
                return account ? account.cbu : null;
            }

            // Formatear fechas
            const [createdFrom, createdTo] = dates || [null, null];
            const createdDates = dates ? [createdFrom, createdTo] : [null, null];

            // Formatear cuenta
            const account = debitAccount?.value === "all" ? "TODOS" : debitAccount?.value || "TODOS";
            const cbu = debitAccount?.value !== "all" ? obtenerCbuPorId(debitAccount?.value) : "";

            // Formatear estado
            const statusValue = status?.value === "all" ? "TODOS" : status?.value || "TODOS";

            return {
                createdFrom: createdFrom ? createdFrom.toISOString() : null,
                createdTo: createdTo ? createdTo.toISOString() : null,
                createdDates: createdDates.map((date) => (date ? date.toISOString() : null)),
                account,
                seller: cuit || "",
                status: statusValue,
                pageNumber: 1,
                cbu,
            };
        },
        [accounts],
    );

    const currentFormValues = filterRecurrencyProgrammed.formValues || INITIAL_FORM_VALUES;

    const handleConfirmAction = (id, action) => {
        const actionMap = {
            enable: "activar",
            disable: "desactivar",
            authorize: "autorizar",
            decline: "rechazar",
        };
        const debinDetail = programmedRecurrencies.find((object) => object.id === id);
        dispatch(debinActions.setRecurrencyDebinAction({ action: actionMap[action], debin: debinDetail }));
        dispatch(debinActions.debinSeller(debinDetail?.vendedor?.cuit));
        dispatch(push("/v2/debin/scheduled/confirmation"));
    };

    const handleDebinAction = (id, action) => {
        const debinDetail = programmedRecurrencies.find((object) => object.id === id);
        if (debinDetail.debin.concepto === "PRE" && (action === "enable" || action === "authorize")) {
            dispatch(
                notificationActions.showNotification(
                    i18n.get(`debin.programmed.validation.concept.${action}.warning`),
                    "warning",
                    ["debin.scheduled"],
                ),
            );
        } else if (action === "enable" || action === "authorize") {
            setSelectedDebin({ id, action });
            setIsModalWarnVisible(true);
        } else {
            handleConfirmAction(id, action);
        }
    };

    const handleDebinActionWithPending = useCallback(
        (id, action) => {
            const actionMap = {
                enable: "activar",
                disable: "desactivar",
                authorize: "autorizar",
                decline: "rechazar",
            };
            const debinDetail = programmedRecurrencies.find((object) => object.id === id);
            const spanishAction = actionMap[action]?.toUpperCase();
            const transaccion = debinDetail.transactions?.find((tx) => tx.action === spanishAction);
            if (transaccion) {
                setSubmitting(true);
                dispatch(debinActions.acceptIncomingRequestPrePending(transaccion?.idTransaction, true));
                return;
            }

            handleDebinAction(id, action);
        },
        [programmedRecurrencies, dispatch, handleDebinAction, setSubmitting],
    );

    const mappedRecurrencies = programmedRecurrencies
        .filter((obj) => {
            const currentStatus = getStatusToDisplay(obj);
            const selectedStatus =
                currentFormValues?.status?.value === "all" ? "TODOS" : currentFormValues?.status?.value;
            return selectedStatus === "TODOS" || selectedStatus === currentStatus;
        })
        .map((item) => {
            const hasPendingSignature = isShowPendingSignature(item);

            return {
                autorizated: item.autorizado,
                buyer: {
                    cbu: item.comprador.cbu,
                    cuit: item.comprador.cuit,
                    name: item.comprador.nombre,
                },
                confirmation_date: item.fecha_confirmacion,
                creation_date: item.fecha_creacion,
                cuit_creacion: item.cuit_creacion,
                debin: {
                    amount: item.debin.importe,
                    concept: item.debin.concepto,
                    currency: item.debin.moneda,
                    detail: item.debin.detalle,
                    limit_part: item.debin.limite_cuotas,
                    reference: item.debin.referencia,
                    service: item.debin.prestacion,
                },
                id: item.id,
                seller: {
                    cuit: item.vendedor.cuit,
                },
                status: getStatusToDisplay(item),
                debitAccount: obtenerDescripcionPorCBU(item.comprador.cbu),
                transactions: item.transactions || [],
                hasPendingSignature: hasPendingSignature ? i18n.get("payments.debin.pending") : hasPendingSignature,
            };
        });

    const mappedAccounts = accounts.map((account) => ({
        amount: account.balance,
        currency: i18n.get(`currency.label.${account.currency}`),
        idProduct: account.idProduct,
        productAlias: account.shortLabel,
    }));

    const getCardLabel = (id) => i18n.get(`debin.programmed.label.card.${id}`);
    const getContextMenuLabel = (id) => i18n.get(`debin.programmed.label.contextMenu.${id}`);

    const getFormLabel = (id) => i18n.get(`debin.programmed.label.form.${id}`);

    useEffect(() => {
        dispatch(debinActions.debinPreActivity());
        const filtersForAPI = formatFiltersForAPI(currentFormValues);
        dispatch(debinActions.getDebinProgrammedRecurrencies(filtersForAPI));
    }, []);

    useEffect(() => {
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            scopes: ["debin.scheduled", "debin"],
            titleKey: "debin.programmed.title",
            goBackPath: "/payments/payDebin",
        });
        return () => {
            if (embeddedInV3Shell) {
                clearEmbeddedV3ShellLayoutState(dispatch);
            }
        };
    }, [dispatch, embeddedInV3Shell, isDesktop]);

    const handleBack = () => {
        if (embeddedInV3Shell) {
            dispatch(push("/payments/payDebin"));
            return;
        }
        dispatch(goBack());
    };

    const setShowFilters = (value) => dispatch(debinActions.setShowFilters(value));

    const handleFormSubmit = useCallback(
        (filters) => {
            const formattedFilters = formatFiltersForAPI(filters);
            if (!isDesktop) {
                dispatch(debinActions.cleanDebinProgrammedRecurrencies());
            }
            dispatch(debinActions.setFilterRecurrencyProgrammed({ formValues: filters }));
            dispatch(debinActions.getDebinProgrammedRecurrencies(formattedFilters));
        },
        [dispatch, formatFiltersForAPI, isDesktop],
    );

    const fetchMoreTransactions = useCallback(
        (pageNumber) => {
            const formattedFilters = formatFiltersForAPI(currentFormValues);
            const filtersWithPage = { ...formattedFilters, pageNumber };

            dispatch(debinActions.setFilterRecurrencyProgrammed(filtersWithPage));
            if (!isDesktop) {
                dispatch(debinActions.getDebinProgrammedRecurrenciesMore(filtersWithPage));
            } else {
                dispatch(debinActions.getDebinProgrammedRecurrencies(filtersWithPage));
            }
        },
        [dispatch, formatFiltersForAPI, currentFormValues],
    );

    const getFilterMessage = () => {
        if (showFilters) {
            return i18n.get("debin.programmed.filter.hideFilters");
        }
        return i18n.get("debin.programmed.filter.showFilters");
    };

    const programedDebinMvp2Enabled = configUtils.getBoolean("frontend.show.programedDebin.mvp2.enable", false);

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="debin.scheduled" />}
            <Box mx={isDesktop ? 40 : 16}>
                {(!embeddedInV3Shell || isDesktop) && (
                    <div className={classNames({ "admin-detail-head": isDesktop }, "px-0")}>
                        {isDesktop && <Head onBack={handleBack} />}
                        <Head
                            title="debin.programmed.title"
                            titleClassName="my-0 align-center"
                            onBack={!isDesktop && handleBack}
                            headerClassName={classNames({ "blue-main-header-mobile": !isDesktop })}
                            rightContent={() => {}}
                            messageTooltip="debin.programmed.hint"
                            hintText="debin.programmed.hint"
                            alwaysShowMessage={isDesktop}
                        />
                    </div>
                )}
                <Box
                    flex
                    justify="end"
                    my={isDesktop ? 24 : 8}
                    ml={isDesktop ? 48 : null}
                    mb={isDesktop && showFilters ? 48 : 0}>
                    <Button
                        text={getFilterMessage()}
                        iconVariant="outline"
                        variant="outline"
                        size="medium"
                        icon={showFilters ? "EyeOff" : "Eye"}
                        onClick={() => setShowFilters(!showFilters)}
                    />
                </Box>
                <Box py={isDesktop ? 24 : null}>
                    <DebinScheduled
                        loading={fetching || submitting}
                        paginationProps={{
                            pageNumber: filterRecurrencyProgrammed?.pageNumber || 1,
                            totalPages: filterRecurrencyProgrammed?.paginas_totales || 1,
                            action: fetchMoreTransactions,
                        }}
                        accountsOptions={mappedAccounts}
                        contextMenuAction={(id, action) => {
                            handleDebinActionWithPending(id, action);
                        }}
                        debinsArray={mappedRecurrencies}
                        emptyStateText={i18n.get("debin.programmed.label.card.empty")}
                        getCardLabel={(id) => getCardLabel(id)}
                        getContextMenuLabel={(id) => getContextMenuLabel(id)}
                        getFormLabel={(id) => getFormLabel(id)}
                        statusOptions={statuses}
                        goToDetail={(id) => dispatch(push(`/v2/debin/scheduled/detail/${id}`))}
                        initialValues={currentFormValues}
                        onSubmit={handleFormSubmit}
                        showForm={showFilters}
                        showEnableDisable={programedDebinMvp2Enabled}
                        validationSchema={Yup.object({
                            dates: Yup.mixed().test(
                                "dates",
                                i18n.get("debin.programmed.validation.dates.required"),
                                ([from, to]) => from && to,
                            ),
                        })}
                        extraInactiveCondition={(values) => {
                            const result = evalEqualsValues(
                                {
                                    ...currentFormValues,
                                    debitAccount: currentFormValues.debitAccount?.value,
                                    status: currentFormValues.status?.value,
                                },
                                {
                                    ...values,
                                    debitAccount: values.debitAccount?.value,
                                    status: values.status?.value,
                                },
                                undefined,
                                ["dates"],
                            );
                            return result;
                        }}
                        hideAmoutsAccounts={hideAmounts}
                    />
                </Box>
            </Box>
            <ModalWarn
                isVisible={isModalWarnVisible}
                titleText={i18n.get("debin.programmed.warning.modal.titleText")}
                descriptionText={i18n.get("debin.programmed.warning.modal.descriptionText")}
                handleConfirm={() => handleConfirmAction(selectedDebin.id, selectedDebin.action)}
                handleCancel={() => setIsModalWarnVisible(false)}
            />
        </>
    );
}

const mapStateToProps = (state) => ({
    programmedRecurrencies: debinSelectors.getProgrammedRecurrencies(state),
    filterRecurrencyProgrammed: debinSelectors.getFilterRecurrencyProgrammed(state),
    accounts: debinSelectors.getAccounts(state),
    showFilters: debinSelectors.getShowFilters(state),
    hideAmounts: debinSelectors.getShowFilters(state),
    fetching: debinSelectors.getFetching(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(ProgrammedDebin);
